import React, { useState, useEffect } from 'react';
import { Button, Card } from './common';
import ProjectInitialization from './ProjectInitialization';
import ProjectList from './ProjectList';
import ProblemDefinition from './ProblemDefinition';
import RootCauseIdentification from './RootCauseIdentification';
import CorrectiveActionPlan from './CorrectiveActionPlan';
import ReportGenerator from './ReportGenerator';
import AIAssistant from './AIAssistant';
import Glossary from './Glossary';
import Header from './common/Header';
import RCANavigation from './common/RCANavigation';
import { checkSupabaseConnection } from '../supabaseClient';
import { theme } from '../styles/theme';
import { Book } from 'lucide-react';

const NoProjectMessage = () => (
  <Card className="text-center py-8">
    <div className={`text-lg text-[${theme.colors.text.secondary}] mb-4`}>
      Please create or select a project first
    </div>
    <div className={`text-sm text-[${theme.colors.text.secondary}]`}>
      Use the Project Initialization tab to get started
    </div>
  </Card>
);

const RootCauseAnalysisPro = ({ user, onUnsavedChanges, standalone = true, setUser }) => {
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('activeTab');
    return savedTab || 'project-init';
  });

  const [project, setProject] = useState(() => {
    const savedProject = localStorage.getItem('currentProject');
    return savedProject ? JSON.parse(savedProject) : null;
  });

  const [pageContext, setPageContext] = useState({
    problemStatement: '',
    currentStep: '',
    formData: null
  });

  const [isSupabaseConnected, setIsSupabaseConnected] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [isGlossaryOpen, setIsGlossaryOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (project) {
      localStorage.setItem('currentProject', JSON.stringify(project));
    } else {
      localStorage.removeItem('currentProject');
    }
  }, [project]);

  useEffect(() => {
    const checkConnection = async () => {
      const isConnected = await checkSupabaseConnection();
      setIsSupabaseConnected(isConnected);
      if (!isConnected) {
        setConnectionError('Unable to connect to the database. Please check your internet connection and try again.');
      }
    };
    checkConnection();
  }, []);

  const updatePageContext = (newContext) => {
    setPageContext(prev => ({
      ...prev,
      ...newContext
    }));
  };

  const handleProjectInit = (newProject) => {
    setProject(newProject);
    setActiveTab('problem-definition');
    updatePageContext({
      currentStep: 'problem-definition',
      formData: null,
      problemStatement: ''
    });
  };

  const handleProjectSelect = (selectedProject) => {
    setProject(selectedProject);
    setActiveTab('problem-definition');
    updatePageContext({
      currentStep: 'problem-definition',
      formData: null,
      problemStatement: ''
    });
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'project-init' || project) {
      setActiveTab(tabId);
    }
  };

  const shouldShowGlossary = () => {
    if (!user) return false;
    if (activeTab === 'project-init' && !project) return false;
    if (showReport) return false;
    return true;
  };

  const renderContent = () => {
    if (!project && activeTab !== 'project-init') {
      return <NoProjectMessage />;
    }

    const commonProps = {
      updatePageContext: updatePageContext
    };

    switch (activeTab) {
      case 'project-init':
        return (
          <div className="space-y-8">
            <ProjectInitialization 
              onProjectInit={handleProjectInit}
              {...commonProps}
            />
            <ProjectList 
              onProjectSelect={handleProjectSelect}
              onFetchError={(error) => setConnectionError(error)}
              {...commonProps}
            />
          </div>
        );
      case 'problem-definition':
        return (
          <ProblemDefinition 
            projectId={project.id}
            {...commonProps}
          />
        );
      case 'root-cause-identification':
        return (
          <RootCauseIdentification 
            projectId={project.id}
            {...commonProps}
          />
        );
      case 'corrective-action-plan':
        return (
          <CorrectiveActionPlan 
            projectId={project.id}
            {...commonProps}
          />
        );
      case 'report-generator':
        return (
          <ReportGenerator 
            projectId={project.id}
            {...commonProps}
          />
        );
      default:
        return null;
    }
  };

  const renderNavigation = () => {
    if (standalone) {
      return (
        <>
          <Header user={user} setUser={setUser} title="RCA_Pro" />
          <RCANavigation 
            activeTab={activeTab}
            onTabChange={handleTabChange}
            project={project}
            standalone={true}
          />
        </>
      );
    } else {
      return (
        <RCANavigation 
          activeTab={activeTab}
          onTabChange={handleTabChange}
          project={project}
          standalone={false}
        />
      );
    }
  };

  return (
    <div className="min-h-screen relative bg-white">
      {renderNavigation()}
      
      <div className="container mx-auto px-4 pb-16">
        <main className="bg-white border rounded-xl shadow-lg p-8 mb-8" style={{ borderColor: theme.colors.border.light }}>
          {isSupabaseConnected ? renderContent() : <p>Connecting to the database...</p>}
        </main>

        {user && project && !showReport && (
          <AIAssistant 
            activeTab={activeTab}
            pageContext={pageContext}
            project={project}
          />
        )}

        {shouldShowGlossary() && (
          <Button
            onClick={() => setIsGlossaryOpen(true)}
            className="fixed bottom-4 left-4 text-white"
            variant="accent"
            icon={Book}
          >
            Glossary
          </Button>
        )}

        <Glossary isOpen={isGlossaryOpen} onClose={() => setIsGlossaryOpen(false)} />
      </div>
    </div>
  );
};

export default RootCauseAnalysisPro;
